import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { CreditCard, Snowflake, Plus, Eye, EyeOff } from "lucide-react";
import { toast, Toaster } from "sonner";

const CardTile = ({ card, revealed, onToggleReveal, onToggleFreeze }) => {
  const dark = card.type === "credit";
  const wrapClasses = `p-6 aspect-[1.6/1] flex flex-col justify-between relative overflow-hidden ${
    dark
      ? "bg-[hsl(var(--primary))] text-white"
      : "bg-[hsl(var(--surface))] border border-[hsl(var(--border))]"
  }`;
  const overlineMuted = dark ? "text-white/60" : "";
  const actionBtnClasses = dark
    ? "p-2 bg-white/10 hover:bg-white/20 text-white"
    : "p-2 bg-[hsl(var(--surface-alt))] hover:bg-[hsl(var(--border))]";

  return (
    <div className={wrapClasses} data-testid={`card-${card.id}`}>
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, rgba(196,101,69,0.5), transparent 50%)",
        }}
      />
      <div className="flex justify-between items-start relative z-10">
        <div>
          <div className={`overline ${overlineMuted}`}>{card.brand}</div>
          <div className="font-display text-lg mt-1 capitalize">{card.type}</div>
        </div>
        <CreditCard className="w-6 h-6 opacity-60" />
      </div>
      <div className="relative z-10">
        <div className="font-mono text-xl tracking-widest">
          {revealed ? `4242 4242 4242 ${card.last4}` : `•••• •••• •••• ${card.last4}`}
        </div>
        <div className="flex justify-between text-xs mt-3 font-mono">
          <div>
            <div className={`overline ${overlineMuted}`}>Holder</div>
            <div>{card.holder_name}</div>
          </div>
          <div>
            <div className={`overline ${overlineMuted}`}>Exp</div>
            <div>{card.expiry}</div>
          </div>
          <div>
            <div className={`overline ${overlineMuted}`}>CVV</div>
            <div>{revealed ? card.cvv : "•••"}</div>
          </div>
        </div>
      </div>
      {card.credit_limit && (
        <div
          className={`absolute top-6 right-12 text-xs font-mono ${
            dark ? "text-white/70" : "text-[hsl(var(--text-muted))]"
          }`}
        >
          Limit {fmtUSD(card.credit_limit)}
        </div>
      )}
      {card.frozen && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm grid place-items-center">
          <div className="text-[hsl(var(--text-main))] font-display text-xl flex items-center gap-2">
            <Snowflake className="w-5 h-5" /> FROZEN
          </div>
        </div>
      )}
      <div className="absolute bottom-3 right-3 flex gap-2 z-10">
        <button
          onClick={() => onToggleReveal(card.id)}
          className={actionBtnClasses}
          data-testid={`reveal-${card.id}`}
        >
          {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onToggleFreeze(card.id)}
          className={actionBtnClasses}
          data-testid={`freeze-${card.id}`}
        >
          <Snowflake className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const IssueCardDialog = ({ open, type, setType, creditLimit, setCreditLimit, hasChecking, busy, onCancel, onSubmit }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onCancel}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-8 w-full max-w-md space-y-5"
      >
        <h2 className="font-display text-2xl">Issue new card</h2>
        <div>
          <label className="overline block mb-2">Type</label>
          <div className="grid grid-cols-2 gap-2">
            {["debit", "credit"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`border px-4 py-3 text-sm capitalize ${
                  type === t
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5"
                    : "border-[hsl(var(--border))]"
                }`}
                data-testid={`card-type-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        {type === "credit" && (
          <div>
            <label className="overline block mb-2">Credit limit</label>
            <input
              type="number"
              min="500"
              step="100"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono"
              data-testid="credit-limit-input"
            />
          </div>
        )}
        {type === "debit" && !hasChecking && (
          <div className="text-sm text-[hsl(var(--destructive))]">
            You need a checking account to issue a debit card.
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="border border-[hsl(var(--border))] px-4 py-2 text-sm"
            data-testid="cancel-issue-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm"
            data-testid="confirm-issue-btn"
          >
            {busy ? "Issuing…" : "Issue card"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Cards = () => {
  const [cards, setCards] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [openIssue, setOpenIssue] = useState(false);
  const [type, setType] = useState("debit");
  const [creditLimit, setCreditLimit] = useState("5000");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, a] = await Promise.all([api.get("/cards"), api.get("/accounts")]);
    setCards(c.data);
    setAccounts(a.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/cards", { type, credit_limit: parseFloat(creditLimit) });
      toast.success(`${type} card issued`);
      setOpenIssue(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Issue failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleFreeze = useCallback(async (id) => {
    try {
      await api.post(`/cards/${id}/toggle-freeze`);
      const { data } = await api.get("/cards");
      setCards(data);
    } catch {
      toast.error("Failed to toggle freeze");
    }
  }, []);

  const toggleReveal = useCallback((id) => {
    setRevealed((s) => ({ ...s, [id]: !s[id] }));
  }, []);

  const hasChecking = useMemo(
    () => accounts.some((a) => a.type === "checking"),
    [accounts]
  );

  return (
    <div className="space-y-8">
      <Toaster richColors position="top-right" />
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Issuing</div>
          <h1 className="font-display text-4xl mt-1">Cards</h1>
        </div>
        <button
          onClick={() => setOpenIssue(true)}
          className="bg-[hsl(var(--primary))] text-white px-4 py-2.5 text-sm hover:bg-[hsl(var(--primary))]/90 flex items-center gap-2"
          data-testid="issue-card-btn"
        >
          <Plus className="w-4 h-4" /> Issue card
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((c) => (
          <CardTile
            key={c.id}
            card={c}
            revealed={!!revealed[c.id]}
            onToggleReveal={toggleReveal}
            onToggleFreeze={toggleFreeze}
          />
        ))}
        {cards.length === 0 && (
          <div className="card-tech p-10 text-center text-[hsl(var(--text-muted))] col-span-full">
            No cards issued. Issue a debit or credit card to get started.
          </div>
        )}
      </div>

      <IssueCardDialog
        open={openIssue}
        type={type}
        setType={setType}
        creditLimit={creditLimit}
        setCreditLimit={setCreditLimit}
        hasChecking={hasChecking}
        busy={busy}
        onCancel={() => setOpenIssue(false)}
        onSubmit={issue}
      />
    </div>
  );
};

export default Cards;
